import React, { useState, useRef, useEffect } from 'react';
import { X, Gift, Sparkles, Loader2, PartyPopper, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Vibrant colors for wheel segments
const SEGMENT_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
  '#FF9FF3', // Pink
  '#54A0FF', // Sky Blue
];

const SpinWheelPopup = ({ onClose, onCodeWon }) => {
  // Stages: loading -> spin -> spinning -> claim -> won
  const [stage, setStage] = useState('loading');
  const [wheelCodes, setWheelCodes] = useState([]);
  const [wheelSegments, setWheelSegments] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [wonCode, setWonCode] = useState(null);
  const [wonDiscount, setWonDiscount] = useState(null);
  const [copied, setCopied] = useState(false);
  const wheelRef = useRef(null);

  // Auto-fill from NavMind chat lead data
  useEffect(() => {
    const navmindLead = localStorage.getItem('navmind_lead');
    if (navmindLead) {
      try {
        const lead = JSON.parse(navmindLead);
        setForm(prev => ({
          ...prev,
          name: lead.name || prev.name,
          phone: lead.phone || prev.phone
        }));
      } catch (e) {
        console.log('Could not parse navmind lead');
      }
    }
  }, []);

  // Fetch wheel codes from backend on mount
  useEffect(() => {
    const fetchWheelCodes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/spin-wheel-codes`);
        if (res.ok) {
          const codes = await res.json();
          if (codes && codes.length > 0) {
            setWheelCodes(codes);
            // Create wheel segments - duplicate codes to fill 8 segments for visual appeal
            const segments = [];
            let colorIndex = 0;
            // Fill up to 8 segments by cycling through available codes
            for (let i = 0; i < 8; i++) {
              const codeData = codes[i % codes.length];
              segments.push({
                label: `${codeData.discount}% OFF`,
                color: SEGMENT_COLORS[colorIndex % SEGMENT_COLORS.length],
                discount: codeData.discount,
                code: codeData.code,
              });
              colorIndex++;
            }
            setWheelSegments(segments);
            setStage('spin');
          } else {
            // No wheel codes configured - close popup
            toast.info('Spin wheel is currently unavailable');
            onClose();
          }
        } else {
          throw new Error('Failed to fetch');
        }
      } catch (err) {
        console.error('Failed to load wheel codes:', err);
        toast.error('Could not load spin wheel');
        onClose();
      }
    };
    fetchWheelCodes();
  }, [onClose]);

  // Spin the wheel
  const handleSpin = () => {
    if (wheelSegments.length === 0) return;
    
    // Randomly select a segment
    const randomIndex = Math.floor(Math.random() * wheelSegments.length);
    const selectedSegment = wheelSegments[randomIndex];
    
    setWonDiscount(selectedSegment.discount);
    setWonCode(selectedSegment.code);
    
    // Set stage to spinning immediately
    setStage('spinning');
    
    // Calculate rotation to land on the winning segment
    const segmentAngle = 360 / wheelSegments.length;
    const segmentCenterFromTop = randomIndex * segmentAngle + segmentAngle / 2;
    const spins = 5; // Number of full rotations for drama
    const finalRotation = spins * 360 + (360 - segmentCenterFromTop);
    
    // Start from current rotation and spin to final
    setRotation(finalRotation);
    
    // After wheel animation completes (4 seconds), show claim form
    setTimeout(() => {
      setStage('claim');
    }, 4200);
  };

  // Submit form to claim the code
  const handleClaim = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill all fields to claim your discount');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/[^0-9]/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setSubmitting(true);
    try {
      // Send lead data to backend
      const res = await fetch(`${API_URL}/api/spin-wheel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          discount: wonDiscount,
          code: wonCode,
        }),
      });
      const data = await res.json();
      
      if (data.already_played) {
        // User already played before - show their existing code
        setWonCode(data.code);
        setWonDiscount(data.discount);
        toast.info(data.message);
      } else {
        // Store form data for auto-fill later
        localStorage.setItem('spinWheelLead', JSON.stringify(form));
      }
      
      // Show the won stage with revealed code
      setStage('won');
      if (onCodeWon) onCodeWon(wonCode);
      
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(wonCode);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const drawWheel = () => {
    if (wheelSegments.length === 0) return null;
    
    const segments = wheelSegments;
    const segmentAngle = 360 / segments.length;
    
    return (
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3"/>
          </filter>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Outer ring */}
        <circle cx="100" cy="100" r="98" fill="none" stroke="#d4a574" strokeWidth="4" filter="url(#shadow)" />
        
        {/* Wheel segments */}
        <g filter="url(#shadow)">
          {segments.map((segment, i) => {
            const startAngle = (i * segmentAngle - 90) * (Math.PI / 180);
            const endAngle = ((i + 1) * segmentAngle - 90) * (Math.PI / 180);
            const x1 = 100 + 95 * Math.cos(startAngle);
            const y1 = 100 + 95 * Math.sin(startAngle);
            const x2 = 100 + 95 * Math.cos(endAngle);
            const y2 = 100 + 95 * Math.sin(endAngle);
            const largeArc = segmentAngle > 180 ? 1 : 0;
            
            // Text position (middle of segment)
            const textAngle = ((i + 0.5) * segmentAngle - 90) * (Math.PI / 180);
            const textX = 100 + 60 * Math.cos(textAngle);
            const textY = 100 + 60 * Math.sin(textAngle);
            const textRotation = i * segmentAngle;
            
            return (
              <g key={i}>
                <path
                  d={`M 100 100 L ${x1} ${y1} A 95 95 0 ${largeArc} 1 ${x2} ${y2} Z`}
                  fill={segment.color}
                  stroke="#fff"
                  strokeWidth="1"
                />
                <text
                  x={textX}
                  y={textY}
                  fill="#333"
                  fontSize="11"
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                  style={{ textShadow: '0 1px 2px rgba(255,255,255,0.8)' }}
                >
                  {segment.label}
                </text>
              </g>
            );
          })}
        </g>
        
        {/* Center circle */}
        <circle cx="100" cy="100" r="20" fill="linear-gradient(135deg, #f5e6d3, #d4a574)" stroke="#c49a6c" strokeWidth="2" filter="url(#glow)" />
        <circle cx="100" cy="100" r="15" fill="#f8f4f0" stroke="#e0d4c4" strokeWidth="1" />
        <text x="100" y="100" fill="#8b7355" fontSize="8" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">SPIN</text>
      </svg>
    );
  };

  // Loading state
  if (stage === 'loading') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading spin wheel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="spin-wheel-close-btn"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-200/30 to-transparent rounded-full -translate-x-16 -translate-y-16" />
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-orange-200/30 to-transparent rounded-full translate-x-20 translate-y-20" />

        <div className="relative p-6">
          {/* Spin / Spinning Stage */}
          {(stage === 'spin' || stage === 'spinning') && (
            <>
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
                  <Gift className="w-4 h-4" />
                  Lucky Draw!
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Spin to Win!</h2>
                <p className="text-gray-600 text-sm mt-1">Get up to {Math.max(...wheelSegments.map(s => s.discount))}% off your AI itinerary</p>
              </div>

              {/* Wheel */}
              <div className="relative w-64 h-64 mx-auto my-4">
                {/* Pointer at top */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 z-10">
                  <div className="w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[20px] border-t-amber-600 drop-shadow-lg" />
                </div>
                
                {/* Spinning wheel */}
                <div
                  ref={wheelRef}
                  className="w-full h-full"
                  style={{ 
                    transform: `rotate(${rotation}deg)`,
                    transition: stage === 'spinning' ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
                  }}
                >
                  {drawWheel()}
                </div>
              </div>

              <Button
                onClick={handleSpin}
                disabled={stage === 'spinning'}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 text-lg rounded-xl shadow-lg disabled:opacity-70"
              >
                {stage === 'spinning' ? (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                    Spinning...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Spin the Wheel!
                  </>
                )}
              </Button>
            </>
          )}

          {/* Claim Stage */}
          {stage === 'claim' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <PartyPopper className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">
                  You won {wonDiscount}% OFF! 🎉
                </h2>
                <p className="text-gray-600 text-sm mt-2">
                  Enter your details to reveal your exclusive code
                </p>
              </div>

              <form onSubmit={handleClaim} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-white/80"
                    required
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-white/80"
                    required
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-white/80"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-5 rounded-xl shadow-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Claiming...
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Reveal My Code
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Won Stage */}
          {stage === 'won' && (
            <>
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl animate-bounce">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Congratulations! 🎊
                </h2>
                <p className="text-gray-600 mb-6">
                  Your {wonDiscount}% discount code is:
                </p>
                
                {/* Code display */}
                <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-4 mb-6 border-2 border-dashed border-amber-300">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-3xl font-bold text-amber-700 tracking-wider">
                      {wonCode}
                    </span>
                    <button
                      onClick={copyCode}
                      className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow"
                    >
                      {copied ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <Copy className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  Use this code at checkout to get {wonDiscount}% off!
                </p>

                <Button
                  onClick={() => {
                    onClose();
                  }}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-5 rounded-xl shadow-lg"
                >
                  Continue to Booking
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpinWheelPopup;
